import { GoogleGenAI } from "@google/genai";
import { documentTypes } from "../documentTypes";

// Initialize the Gemini API with the provided API key from environment variables
const API_KEY = process.env.REACT_APP_GEMINI_API_KEY;
const ai = new GoogleGenAI({ apiKey: API_KEY });

/**
 * Validates a document using Google Gemini AI
 * @param {File} file - The document file to validate
 * @param {string} documentType - The type of document being validated
 * @returns {Promise<Object>} - The validation result in JSON format
 */
export const validateDocumentWithGemini = async (file, documentType) => {
  try {
    console.log("validateDocumentWithGemini called with:", {
      fileExists: !!file,
      fileType: file?.type,
      fileSize: file?.size,
      documentType
    });

    // Check if file is valid
    if (!file) {
      console.error("File is undefined or null");
      throw new Error("No file provided");
    }

    if (!file.type) {
      console.error("File type is undefined");
      throw new Error("File type is undefined");
    }

    if (file.size === undefined) {
      console.error("File size is undefined");
      throw new Error("File size is undefined");
    }

    console.log("File validation passed:", { type: file.type, size: file.size });

    // Convert the file to a base64 string
    console.log("Converting file to base64...");
    const base64Data = await fileToBase64(file);
    console.log("Base64 data created successfully, length:", base64Data?.length);

    // Create the prompt for Gemini
    const prompt = generateDocumentPrompt(documentType);

    console.log("Preparing to generate content with Gemini...");
    try {
      console.log("Calling Gemini API with:", {
        model: "gemini-2.0-flash",
        promptLength: prompt?.length,
        base64DataLength: base64Data?.length
      });

      // Create content with inline image data
      const contents = [
        {
          parts: [
            {
              inlineData: {
                mimeType: file.type,
                data: base64Data
              }
            },
            { text: prompt }
          ]
        }
      ];

      const response = await ai.models.generateContent({
        model: "gemini-2.0-flash",
        contents: contents
      });

      console.log("Gemini API response received:", {
        responseExists: !!response,
        hasText: !!response?.text,
        textLength: response?.text?.length
      });

      // Extract and parse the JSON from the response
      try {
        console.log("Raw response text:", response.text);

        // The response structure is different - we need to extract the JSON from the text
        // The JSON is in the first candidate's content parts text
        let jsonText = '';

        if (response.candidates &&
            response.candidates[0] &&
            response.candidates[0].content &&
            response.candidates[0].content.parts &&
            response.candidates[0].content.parts[0] &&
            response.candidates[0].content.parts[0].text) {

          jsonText = response.candidates[0].content.parts[0].text;
          console.log("Found JSON text in response structure");
        } else if (response.text) {
          // Fallback to response.text if available
          jsonText = response.text;
          console.log("Using response.text as fallback");
        } else {
          throw new Error("Unexpected response structure from Gemini API");
        }

        // The JSON might be wrapped in markdown code blocks, remove them
        jsonText = jsonText.replace(/```json\n/g, '').replace(/```/g, '');
        console.log("Cleaned JSON text:", jsonText.substring(0, 100) + (jsonText.length > 100 ? '...' : ''));

        // Now parse the JSON
        const jsonResponse = JSON.parse(jsonText);
        console.log("Successfully parsed JSON response:", {
          documentType: jsonResponse?.documentType,
          issuesCount: jsonResponse?.issues?.length,
          informationCount: jsonResponse?.information?.length
        });

        return jsonResponse;
      } catch (parseError) {
        console.error("Error extracting or parsing JSON from Gemini response:", parseError);
        console.log("Raw response that failed to parse:", response);

        return {
          documentType: documentType,
          issues: ["Could not parse AI response as JSON"],
          comment: "Error processing document with AI",
          accuracyOfData: 0,
          information: []
        };
      }
    } catch (apiError) {
      console.error("Error calling Gemini API:", apiError);
      console.log("API error details:", {
        name: apiError.name,
        message: apiError.message,
        stack: apiError.stack
      });

      throw new Error(`Failed to process with Gemini API: ${apiError.message}`);
    }
  } catch (error) {
    console.error("Error validating document with Gemini:", error);
    console.log("Error details:", {
      name: error.name,
      message: error.message,
      stack: error.stack
    });

    throw new Error(`Failed to validate document: ${error.message}`);
  }
};


function generateDocumentPrompt(documentTypeValue) {
  const documentTypeObject = documentTypes.find(dt => dt.value === documentTypeValue);
  const documentTypeLabel = documentTypeObject ? documentTypeObject.label : "document";

  let verificationDetails = `First, confirm that the provided image is indeed a ${documentTypeLabel} and not a different type of document. Then, check for any signs of tampering, inconsistencies, or issues that might indicate it's not genuine (e.g., mismatched fonts, unusual layouts, pixelation around key text, altered dates, spelling errors in official text).`;
  let extractionDetails = "Extract any relevant information from the document such as names, dates, document numbers, addresses, issuing authorities, etc.";

  switch (documentTypeValue) {
      case "PASSPORT":
      case "AUSTRALIAN_PASSPORT":
      case "FOREIGN_PASSPORT":
          verificationDetails = `First, confirm that the provided image is indeed a ${documentTypeLabel}. Then, carefully verify its authenticity and validity. Pay close attention to security features (holograms, watermarks, microprinting), the Machine Readable Zone (MRZ), photo quality, and the overall layout. Ensure the country of issuance and expiry date are clearly visible, consistent, and valid. Check for any signs of tampering, inconsistencies, or issues.`;
          extractionDetails = `Extract all relevant information, giving special importance to: full name, passport number, issuing country, date of birth, date of issue, and date of expiry. Also extract nationality, sex, and place of birth if visible.`;
          break;

      case "CREDIT_OR_ACCOUNT_CARD": // Bank Card
          verificationDetails = `First, confirm that the provided image is indeed a ${documentTypeLabel}. Then, verify its authenticity and validity. Check for standard card features (e.g., EMV chip, hologram if applicable, bank logo), card number format (length and grouping), and ensure the cardholder's name, expiry date, and bank name are clear, consistent, and appear legitimate. Look for signs of tampering or unusual wear.`;
          extractionDetails = `Extract all relevant information, with high importance on: cardholder name, expiry date, and bank name. Also extract the card number (or last 4 digits if full number extraction is sensitive/configured) and card type (e.g., Visa, Mastercard) if identifiable.`;
          break;

      case "BANK_STATEMENT":
          verificationDetails = `First, confirm that the provided image is indeed a ${documentTypeLabel}. Then, verify its authenticity. Look for typical bank statement layouts, official bank logos, and consistent formatting. Ensure the account holder's name and bank name are clearly visible and match expectations. Check for consistency in dates, transaction patterns (if multiple pages or entries are visible), and look for any signs of alteration or digital manipulation.`;
          extractionDetails = `Extract all relevant information, with high importance on: account holder's name and the bank name. Also extract statement date, account number, address, and period covered by the statement.`;
          break;

      case "FULL_BIRTH_CERTIFICATE":
      case "BIRTH_CERTIFICATE_EXTRACT":
      case "BIRTH_CARD":
          verificationDetails = `First, confirm that the provided image is indeed a ${documentTypeLabel}. Then, verify its authenticity and validity. Check for official seals, watermarks, specific paper type (if discernible), registrar signatures, and state/territory-specific formatting. Ensure the name of the individual, date of birth, and place of birth are clear and consistent. Look for any signs of alteration.`;
          extractionDetails = `Extract all relevant information, with high importance on: the full name of the individual. Also extract date of birth, place of birth, parents' names (if present), registration number, date of issue, and issuing state/authority.`;
          break;

      case "CITIZENSHIP_CERTIFICATE":
      case "AUSTRALIAN_CITIZENSHIP_CERTIFICATE":
          verificationDetails = `First, confirm that the provided image is indeed a ${documentTypeLabel}. Then, verify its authenticity and validity. Look for official government seals, specific security features, and appropriate government department branding. Ensure the name of the individual and the country of citizenship (e.g., Australia) are clearly stated and consistent. Check for any signs of tampering.`;
          extractionDetails = `Extract all relevant information, with high importance on: the full name of the individual and the country of citizenship. Also extract date of birth, certificate number, date of acquisition/grant of citizenship, and any other identifying numbers.`;
          break;

      case "LICENSE": // Generic Driver's License
      case "DRIVER_LICENSE_AUS":
          verificationDetails = `First, confirm that the provided image is indeed a ${documentTypeLabel}. Then, verify its authenticity and validity. Check for state/territory-specific security features (e.g., holograms, microprinting), photo quality, and the overall layout. Ensure the name, address, date of birth, license number, issuing state/authority, and expiry date are clear, consistent, and valid. Look for signs of tampering.`;
          extractionDetails = `Extract all relevant information, with high importance on: full name, license number, issuing state/authority, and expiry date. Also extract date of birth, address, license class, and issue date.`;
          break;

      // "Job ID" is generic, mapping to specific ID types
      case "GOVERNMENT_EMPLOYEE_ID":
      case "DEFENCE_ID_CARD":
          verificationDetails = `First, confirm that the provided image is indeed a ${documentTypeLabel}. Then, verify its authenticity. Look for official insignia, department/agency name, photo quality, and security features specific to such IDs. Ensure the employee's name, ID number, issuing authority/organisation, and expiry date (if applicable) are clear and consistent. Check for signs of tampering.`;
          extractionDetails = `Extract all relevant information, with high importance on: employee name, issuing organisation/department, country of the organisation (if a national entity like Defence Force), and expiry date. Also extract employee ID number and date of issue.`;
          break;
      
      case "TERTIARY_STUDENT_ID_CARD":
          verificationDetails = `First, confirm that the provided image is indeed a ${documentTypeLabel}. Then, assess its validity. Check for the educational institution's logo, student photo, and ensure the student's name, student ID number, and validity period (e.g., issue/expiry date, academic year) are clear, consistent, and appear legitimate for a student ID.`;
          extractionDetails = `Extract all relevant information, with high importance on: student's full name and the validity period of the card (e.g., expiry date, academic year). Also extract student ID number, name of the educational institution, and date of issue if present.`;
          break;

      case "MARRIAGE_CERTIFICATE":
          verificationDetails = `First, confirm that the provided image is indeed a ${documentTypeLabel}. Then, verify its authenticity. Check for official seals, registrar signatures, and state/territory-specific formatting. Ensure the names of the individuals married, date of marriage, and place of marriage are clear and consistent. Look for any signs of alteration.`;
          extractionDetails = `Extract all relevant information, with high importance on: the full names of both individuals married (bride and groom, or spouse 1 and spouse 2) and the issuing state/territory. Also extract date of marriage, place of marriage, certificate number, and date of registration/issue.`;
          break;

      case "MEDICARE_CARD":
          verificationDetails = `First, confirm that the provided image is indeed a ${documentTypeLabel}. Then, verify its validity. Check for the standard Medicare card layout, ensure the names of individuals covered, Medicare number, individual reference number (IRN), and expiry date are clear, valid, and consistent.`;
          extractionDetails = `Extract all relevant information, with high importance on: full names of all individuals listed on the card and the card's expiry date. Also extract the Medicare card number and the Individual Reference Number (IRN) for each person.`;
          break;

      case "CHANGE_OF_NAME_CERTIFICATE":
          verificationDetails = `First, confirm that the provided image is indeed a ${documentTypeLabel}. Then, verify its authenticity. Check for official seals, court or registry details, and issuing state/territory-specific formatting. Ensure the previous name, new name, date of change, and issuing state/authority are clear and consistent. Look for signs of alteration.`;
          extractionDetails = `Extract all relevant information, with high importance on: the previous full name, the new full name, and the issuing state/territory. Also extract date of name change, certificate number, and date of issue/registration.`;
          break;

      case "UTILITY_BILL": // Covers phone bill, electricity, gas, water
      case "RATING_AUTHORITY_NOTICE": // Council rates
          verificationDetails = `First, confirm that the provided image is indeed a ${documentTypeLabel}. Then, assess its authenticity. Look for provider logos, service address, account holder name, billing period, and amounts. Ensure the document appears to be a genuine bill from a recognized provider or authority and is recent (usually within the last 3-6 months). Check for signs of digital alteration.`;
          extractionDetails = `Extract all relevant information, with high importance on: the account holder's full name and the name of the service provider/authority. Also extract service address, billing date or issue date, account number, and billing period.`;
          break;

      case "PHOTO_ID_CARD": // e.g., Photo ID Card (AUS Regulatory)
      case "CERTIFICATE_OF_IDENTITY": // e.g., Certificate of Identity (AUS)
      // For "Proof of Age Card" (not explicitly in list but similar)
          verificationDetails = `First, confirm that the provided image is indeed a ${documentTypeLabel}. Then, verify its authenticity and validity. Check for issuing authority, security features (if any, e.g., holograms), photo quality, and overall layout. Ensure the name, date of birth, card number (if any), issuing state/authority, and expiry date are clear, consistent, and valid. Look for signs of tampering.`;
          extractionDetails = `Extract all relevant information, with high importance on: full name, expiry date, and issuing state/authority. Also extract date of birth, card number, and date of issue.`;
          break;
      
      case "LEASE_AGREEMENT":
      case "PROPERTY_LEASE_AGREEMENT": // Assuming this is the intent for property lease
           verificationDetails = `First, confirm that the provided image is indeed a ${documentTypeLabel}. Then, assess its authenticity. Check for standard lease agreement clauses, names and signatures of lessor(s) and lessee(s), property address, lease term (start and end dates), and rent amount. Ensure it appears to be a complete and legitimate agreement. Pay attention to the state/jurisdiction if mentioned.`;
           extractionDetails = `Extract all relevant information, with high importance on: full names of all lessors (landlords) and lessees (tenants), the property address, the lease start date, and the lease end date (or term duration). Also extract the rent amount, payment frequency, and the state/jurisdiction of the agreement if specified.`;
           break;

      case "TAXATION_NOTICE": // Tax File Name is important
           verificationDetails = `First, confirm that the provided image is indeed a ${documentTypeLabel} (e.g., Notice of Assessment). Then, verify its authenticity. Look for official government revenue agency branding (e.g., ATO logo), taxpayer name, tax identification number (e.g., TFN), assessment year, and key financial figures. Ensure it looks like an official notice and check for signs of alteration.`;
           extractionDetails = `Extract all relevant information, with high importance on: the taxpayer's full name and Tax File Number (TFN) or other relevant tax ID. Also extract the assessment year/period, notice date, issuing government agency (e.g., Australian Taxation Office), and address.`;
           break;
      
      case "RESUME":
      case "CV":
          verificationDetails = `First, confirm that the provided image is indeed a ${documentTypeLabel}. While not an official document for authenticity in the same way as a passport, assess its structure, professionalism, and consistency. Check for any obvious red flags like placeholder text, highly unusual formatting, or nonsensical job descriptions. The primary goal is information extraction.`;
          extractionDetails = `Extract key information typically found in a ${documentTypeLabel}: applicant's full name, contact information (email, phone, address if present), education history (institution, degree, graduation dates), work experience (company names, job titles, employment dates, responsibilities), skills, and references if included.`;
          break;
      
      case "CERTIFICATE": // Generic certificate
          verificationDetails = `First, confirm that the provided image is indeed a ${documentTypeLabel}. Then, assess its authenticity. Look for issuing organization's name/logo, recipient's name, title of the certificate, date of issue, and any signatures or seals. Check for clarity, professional appearance, and consistency. Look for signs of tampering.`;
          extractionDetails = `Extract all relevant information such as: recipient's full name, title or description of the certificate/achievement, name of the issuing body/organization, date of issue or completion, and any unique certificate or accreditation numbers.`;
          break;
          
      default:
          // Generic fallback for other types
          verificationDetails = `First, confirm that the provided image is indeed a ${documentTypeLabel} (or the specified document type). Then, carefully analyze this document. Check for any signs of tampering, inconsistencies, or issues that might indicate it's not genuine.`;
          extractionDetails = `Extract any relevant information from the document such as names, dates, document numbers, addresses, specific identifiers, issuing entities, etc.`;
          break;
  }

  return `
    You are a document validation expert. Please analyze this ${documentTypeLabel} document carefully.

    ${verificationDetails}

    ${extractionDetails}

    Respond ONLY with a JSON object in exactly this format:
    {
      "documentType": "The type of document detected (e.g., '${documentTypeLabel}')",
      "issues": ["issue 1", "issue 2", ...],
      "comment": "Overall assessment of the document's authenticity and validity, highlighting any specific checks performed based on the document type.",
      "accuracyOfData": 0.95, 
      "information": [
        {"type": "name", "data": "extracted name(s)"},
        {"type": "document_number", "data": "extracted document number"},
        {"type": "date_of_birth", "data": "extracted DOB"},
        {"type": "issue_date", "data": "extracted issue date"},
        {"type": "expiry_date", "data": "extracted expiry date"},
        {"type": "issuing_authority", "data": "extracted issuing authority/country/state/provider"},
        {"type": "address", "data": "extracted address"},
        {"type": "other_specific_info_1_key": "extracted_specific_info_1_value"},
        {"type": "other_specific_info_2_key": "extracted_specific_info_2_value"}
      ]
    }

    For the "information" array, use descriptive "type" keys relevant to the ${documentTypeLabel}. For example, for a Passport, include "passport_number", "issuing_country", "nationality". For a Bank Statement, include "bank_name", "account_number", "statement_date". Ensure all key information identified as important for this document type is extracted. If a piece of information is not present, omit its entry or use a null/empty string value.

    Do not include any other text in your response, only the JSON object.
  `;
}


/**
 * Converts a File object to a base64 string
 * @param {File} file - The file to convert
 * @returns {Promise<string>} - A base64 string representing the file
 */
const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    console.log("fileToBase64 called with:", {
      fileExists: !!file,
      fileType: file?.type,
      fileSize: file?.size,
      fileIsBlob: file instanceof Blob
    });

    if (!file) {
      console.error("File is undefined or null in fileToBase64");
      reject(new Error("Invalid file object"));
      return;
    }

    if (!(file instanceof Blob)) {
      console.error("File is not a Blob or File object:", typeof file);
      reject(new Error("File is not a valid Blob or File object"));
      return;
    }

    try {
      console.log("Creating FileReader...");
      const reader = new FileReader();

      reader.onload = (event) => {
        console.log("FileReader onload triggered");
        if (!event.target?.result) {
          console.error("FileReader result is empty or undefined");
          reject(new Error("FileReader result is empty"));
          return;
        }

        // Convert data URL to base64 by removing the prefix
        const base64Data = event.target.result.split(',')[1];
        console.log("Base64 data extracted, length:", base64Data?.length);

        if (!base64Data) {
          console.error("Failed to extract base64 data from FileReader result");
          reject(new Error("Failed to extract base64 data"));
          return;
        }

        resolve(base64Data);
      };

      reader.onerror = (error) => {
        console.error("FileReader error:", error);
        reject(new Error(`File reading error: ${error.message || 'Unknown error'}`));
      };

      console.log("Calling readAsDataURL...");
      reader.readAsDataURL(file);
      console.log("readAsDataURL called");
    } catch (error) {
      console.error("Exception in fileToBase64:", error);
      reject(new Error(`File processing error: ${error.message}`));
    }
  });
};
